/**
 * Tutorial Configuration
 * Defines all available tutorials with their metadata
 */

// Base URL for tutorial videos from CDN
const VIDEO_BASE_URL = process.env.REACT_APP_VIDEO_ASSETS_BASE_URL || 'https://ds7k9n9pvie16.cloudfront.net/videos';

// Fallback video if CDN URL is not available
const DEFAULT_TUTORIAL_VIDEO = '/assets/landing/SimpleSNF_Overview.mp4';

// Mapping of tutorial feature IDs to their video file names
const VIDEO_FILE_MAP = {
  dashboard_introduction: 'dashboard-introduction-1.mp4',
  default_percentages: 'default-percentages-2.mp4',
  filter_selection: 'filter-selection-3.mp4',
  lock_percentage: 'lock-percentage-4.mp4',
  resident_list: 'resident-list-5.mp4',
  time_graph: 'time-graph-6.mp4',
  generating_reports: 'generating-reports-7.mp4',
  automated_alerts: 'automated-alerts-8.mp4',
  spotting_trends: 'spotting-trends-instantly-9.mp4',
  time_frame_converter: 'time-frame-converter-10.mp4',
  notes_feature: 'notes-feature-11.mp4',
  community_transfers: 'community-transfers-12.mp4',
  deceased_transfers: 'deceased-transfers-13.mp4',
  admissions: 'admissions-13.mp4',
  overall: 'overall-14.mp4',
  transfer_status_admission: 'transfer-status-of-admission-15.mp4',
  multiple_buildings: 'viewing-multiple-facilities-16.mp4',
  ai_chat: null // No video file provided, will use default
};

/**
 * Get video URL for a tutorial feature
 * @param {string} featureId - Tutorial feature ID
 * @returns {string} Video URL
 */
const getVideoUrl = (featureId) => {
  const videoFileName = VIDEO_FILE_MAP[featureId];
  if (videoFileName) {
    return `${VIDEO_BASE_URL}/${videoFileName}`;
  }
  return DEFAULT_TUTORIAL_VIDEO;
};

export const TUTORIAL_FEATURES = {
  DASHBOARD_INTRODUCTION: 'dashboard_introduction',
  DEFAULT_PERCENTAGES: 'default_percentages',
  FILTER_SELECTION: 'filter_selection',
  LOCK_PERCENTAGE: 'lock_percentage',
  RESIDENT_LIST: 'resident_list',
  TIME_GRAPH: 'time_graph',
  GENERATING_REPORTS: 'generating_reports',
  AUTOMATED_ALERTS: 'automated_alerts',
  SPOTTING_TRENDS: 'spotting_trends',
  TIME_FRAME_CONVERTER: 'time_frame_converter',
  NOTES_FEATURE: 'notes_feature',
  COMMUNITY_TRANSFERS: 'community_transfers',
  DECEASED_TRANSFERS: 'deceased_transfers',
  ADMISSIONS: 'admissions',
  OVERALL: 'overall',
  TRANSFER_STATUS_ADMISSION: 'transfer_status_admission',
  MULTIPLE_BUILDINGS: 'multiple_buildings' 
};

export const TUTORIAL_CONFIG = {
  [TUTORIAL_FEATURES.DASHBOARD_INTRODUCTION]: {
    featureId: TUTORIAL_FEATURES.DASHBOARD_INTRODUCTION,
    title: 'Dashboard Introduction',
    description: 'Get started with the dashboard and learn the basics.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.DASHBOARD_INTRODUCTION),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.DEFAULT_PERCENTAGES
  },
  [TUTORIAL_FEATURES.DEFAULT_PERCENTAGES]: {
    featureId: TUTORIAL_FEATURES.DEFAULT_PERCENTAGES,
    title: 'Default Percentages',
    description: 'Learn about default percentages and how they work in the dashboard.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.DEFAULT_PERCENTAGES),
    transcript: null,
    nextFeature: null
  },
  [TUTORIAL_FEATURES.FILTER_SELECTION]: {
    featureId: TUTORIAL_FEATURES.FILTER_SELECTION,
    title: 'Filter Selection',
    description: 'See how to use dashboard filters to focus on the data that matters most.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.FILTER_SELECTION),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.LOCK_PERCENTAGE
  },
  [TUTORIAL_FEATURES.LOCK_PERCENTAGE]: {
    featureId: TUTORIAL_FEATURES.LOCK_PERCENTAGE,
    title: 'Lock Percentage',
    description: 'Learn how to lock percentages to maintain consistent views.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.LOCK_PERCENTAGE),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.TIME_GRAPH
  },
  [TUTORIAL_FEATURES.RESIDENT_LIST]: {
    featureId: TUTORIAL_FEATURES.RESIDENT_LIST,
    title: 'Resident List',
    description: 'Learn how to open and use the resident list view.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.RESIDENT_LIST),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.NOTES_FEATURE
  },
  [TUTORIAL_FEATURES.TIME_GRAPH]: {
    featureId: TUTORIAL_FEATURES.TIME_GRAPH,
    title: 'Time Graph',
    description: 'See how to explore your data using time graph visualizations.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.TIME_GRAPH),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.TIME_FRAME_CONVERTER
  },
  [TUTORIAL_FEATURES.GENERATING_REPORTS]: {
    featureId: TUTORIAL_FEATURES.GENERATING_REPORTS,
    title: 'Generating Reports',
    description: 'Learn how to generate and export reports from the dashboard.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.GENERATING_REPORTS),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.AUTOMATED_ALERTS
  },
  [TUTORIAL_FEATURES.AUTOMATED_ALERTS]: {
    featureId: TUTORIAL_FEATURES.AUTOMATED_ALERTS,
    title: 'Automated Alerts',
    description: 'Understand how automated alerts help you stay informed about important changes.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.AUTOMATED_ALERTS),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.SPOTTING_TRENDS
  },
  [TUTORIAL_FEATURES.SPOTTING_TRENDS]: {
    featureId: TUTORIAL_FEATURES.SPOTTING_TRENDS,
    title: 'Spotting Trends Instantly',
    description: 'Discover how to identify and analyze trends in your data quickly.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.SPOTTING_TRENDS),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.DEFAULT_PERCENTAGES
  },
  [TUTORIAL_FEATURES.TIME_FRAME_CONVERTER]: {
    featureId: TUTORIAL_FEATURES.TIME_FRAME_CONVERTER,
    title: 'Time Frame Converter',
    description: 'Learn how to adjust the dashboard time frame to match your reporting needs.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.TIME_FRAME_CONVERTER),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.SPOTTING_TRENDS
  },
  [TUTORIAL_FEATURES.NOTES_FEATURE]: {
    featureId: TUTORIAL_FEATURES.NOTES_FEATURE,
    title: 'Notes Feature',
    description: 'Discover how to create and manage notes directly from the dashboard.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.NOTES_FEATURE),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.RESIDENT_LIST
  },
  [TUTORIAL_FEATURES.COMMUNITY_TRANSFERS]: {
    featureId: TUTORIAL_FEATURES.COMMUNITY_TRANSFERS,
    title: 'Community Transfers',
    description: 'Get an overview of the Community Transfers dashboard tabs and insights.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.COMMUNITY_TRANSFERS),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.ADMISSIONS
  },
  [TUTORIAL_FEATURES.DECEASED_TRANSFERS]: {
    featureId: TUTORIAL_FEATURES.DECEASED_TRANSFERS,
    title: 'Deceased Transfers',
    description: 'Understand how to review and act on deceased transfer data.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.DECEASED_TRANSFERS),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.OVERALL
  },
  [TUTORIAL_FEATURES.ADMISSIONS]: {
    featureId: TUTORIAL_FEATURES.ADMISSIONS,
    title: 'Admissions',
    description: 'Learn the key workflows within the Admissions dashboard.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.ADMISSIONS),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.TRANSFER_STATUS_ADMISSION
  },
  [TUTORIAL_FEATURES.OVERALL]: {
    featureId: TUTORIAL_FEATURES.OVERALL,
    title: 'Overall',
    description: 'Get an overview of the Overall dashboard and combined metrics.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.OVERALL),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.SPOTTING_TRENDS
  },
  [TUTORIAL_FEATURES.TRANSFER_STATUS_ADMISSION]: {
    featureId: TUTORIAL_FEATURES.TRANSFER_STATUS_ADMISSION,
    title: 'Transfer Status of Admission',
    description: 'Learn about transfer status tracking for admissions.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.TRANSFER_STATUS_ADMISSION),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.GENERATING_REPORTS
  },
  [TUTORIAL_FEATURES.MULTIPLE_BUILDINGS]: {
    featureId: TUTORIAL_FEATURES.MULTIPLE_BUILDINGS,
    title: 'Viewing Multiple Facilities',
    description: 'Understand how to compare data across multiple facilities.',
    videoSrc: getVideoUrl(TUTORIAL_FEATURES.MULTIPLE_BUILDINGS),
    transcript: null,
    nextFeature: TUTORIAL_FEATURES.GENERATING_REPORTS
  }
};

/**
 * Get tutorial config for a feature
 */
export const getTutorialConfig = (featureId) => {
  return TUTORIAL_CONFIG[featureId] || null;
};

/**
 * Get all available tutorials
 */
export const getAllTutorials = () => {
  return Object.values(TUTORIAL_CONFIG);
};

/**
 * Get next tutorial feature ID based on current feature and completion status
 * Handles conditional logic like "if not completed" and skips already watched videos
 */
export const getNextTutorialFeature = async (currentFeatureId, visited = new Set()) => {
  // Prevent infinite loops
  if (visited.has(currentFeatureId)) {
    return null;
  }
  visited.add(currentFeatureId);

  const currentTutorial = TUTORIAL_CONFIG[currentFeatureId];
  if (!currentTutorial) {
    return null;
  }

  const { getTutorialPreference } = await import('../utilis/tutorial-storage');

  // Handle conditional logic for specific features
  let nextFeatureId = null;

  if (currentFeatureId === TUTORIAL_FEATURES.SPOTTING_TRENDS) {
    // Spotting Trends → next: Default Percentages (if not completed)
    const defaultPercentagesPreference = await getTutorialPreference(TUTORIAL_FEATURES.DEFAULT_PERCENTAGES);
    if (defaultPercentagesPreference !== 'watched') {
      nextFeatureId = TUTORIAL_FEATURES.DEFAULT_PERCENTAGES;
    }
  } else if (currentFeatureId === TUTORIAL_FEATURES.NOTES_FEATURE) {
    // Notes Feature → next: Resident List (if not completed)
    const residentListPreference = await getTutorialPreference(TUTORIAL_FEATURES.RESIDENT_LIST);
    if (residentListPreference !== 'watched') {
      nextFeatureId = TUTORIAL_FEATURES.RESIDENT_LIST;
    }
  } else {
    // For all other cases, use the nextFeature directly
    nextFeatureId = currentTutorial.nextFeature;
  }

  // If no next feature, return null
  if (!nextFeatureId) {
    return null;
  }

  // Check if the next video is already watched
  const nextPreference = await getTutorialPreference(nextFeatureId);
  
  // If next video is not watched, return it
  if (nextPreference !== 'watched') {
    return nextFeatureId;
  }

  // If next video is already watched, recursively find the next unwatched video
  return await getNextTutorialFeature(nextFeatureId, visited);
};

